<template>
  <div ref="container"></div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from "vue";
import { TURNSTILE_SITE_KEY } from "../const";
import { loadTurnstileScript } from "../utils/turnstile";

// Turnstile has no separate "invisible" widget type on the client — the size
// prop only controls the visible box's dimensions. Any prior "invisible"
// usage now always renders a visible widget (same as Cloudflare's own
// "Managed" widgets), which the sign-in/sign-up forms have accepted.
const props = defineProps({
  size: {
    type: String,
    default: "normal",
  },
  theme: {
    type: String,
    default: "auto",
  },
});

const emit = defineEmits(["verify", "expired", "error"]);

const container = ref();
const widgetId = ref();

onMounted(() => {
  loadTurnstileScript()
    .then((turnstile) => {
      if (!container.value || widgetId.value) return;
      widgetId.value = turnstile.render(container.value, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: props.theme,
        size: props.size,
        callback: (token) => emit("verify", token),
        "error-callback": (errorCode) => emit("error", errorCode),
        "expired-callback": () => emit("expired"),
      });
    })
    .catch((error) => {
      console.error("[Turnstile]", error);
      emit("error", error?.message);
    });
});

onBeforeUnmount(() => {
  if (widgetId.value && window.turnstile) {
    window.turnstile.remove(widgetId.value);
    widgetId.value = undefined;
  }
});

const reset = () => {
  if (widgetId.value && window.turnstile) {
    window.turnstile.reset(widgetId.value);
  }
};

const getResponse = () => {
  if (widgetId.value && window.turnstile) {
    return window.turnstile.getResponse(widgetId.value) || null;
  }
  return null;
};

defineExpose({
  reset,
  getResponse,
});
</script>
